import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { mockContact } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contacts`, formData);
      
      toast.success("Messaggio inviato!", {
        description: "Ti risponderemo al più presto.",
      });

      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Si è verificato un errore. Riprova.";
      toast.error("Errore nell'invio", {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefono",
      value: mockContact.phone,
      link: `tel:${mockContact.phone}`,
      color: "blue"
    },
    {
      icon: Mail,
      title: "Email",
      value: mockContact.email,
      link: `mailto:${mockContact.email}`,
      color: "orange"
    },
    {
      icon: MapPin,
      title: "Zona Operativa",
      value: `${mockContact.city} e Provincia`,
      color: "blue"
    },
    {
      icon: Clock,
      title: "Orari",
      value: mockContact.workingHours,
      color: "orange"
    }
  ];

  return (
    <section id="contatti" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Contattaci
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Siamo pronti ad ascoltare le tue esigenze e fornirti la soluzione migliore
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                const bgColor = info.color === 'blue' ? 'bg-blue-100' : 'bg-orange-100';
                const textColor = info.color === 'blue' ? 'text-blue-600' : 'text-orange-600';
                
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 ${bgColor} rounded-lg flex items-center justify-center mb-4`}>
                        <Icon className={`w-6 h-6 ${textColor}`} />
                      </div>
                      <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className={`${textColor} hover:underline font-medium`}>
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-600 text-sm">{info.value}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Map or Image */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-64">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80"
                alt="Modena"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Invia un Messaggio</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Messaggio</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={isLoading}>
                  <Send className="w-4 h-4 mr-2" />
                  {isLoading ? 'Invio in corso...' : 'Invia Messaggio'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
